/**
 * One master lesson per concept, two render depths (Coleman, 2026-10-08:
 * "Certification modules must never depend on Platform completion. Treat
 * the Platform and Certification experiences as two render depths of the
 * same master lesson.").
 *
 * Every block in a master is one of:
 *
 *   core      no `depth`: in both versions
 *   cert      depth "cert": the Certification only (deeper sections,
 *             science nerd blocks, case studies, activities)
 *   platform  depth "platform": the Platform only. A core section the
 *             cert swaps for its own deeper version in the same spot, so a
 *             cert reader isn't shown the answer to a case study before
 *             being asked it.
 *
 * The Platform read is the core in one chapter, so it reads straight down.
 * The Certification is everything in order, chaptered, and stands alone: a
 * coach can open it with no Platform read behind them.
 *
 * Pure, so the tests and the registry can both call it.
 */

export const CERT = "cert";
export const PLATFORM = "platform";

const shows = (block, view) => !block.depth || block.depth === view;

/** The Platform read of a master: core plus Platform-only, one chapter. */
export function platformView(master) {
  const p = master.platform;
  const blocks = master.chapters.flatMap(ch => ch.blocks.filter(b => shows(b, PLATFORM)));
  return {
    key: p.key,
    title: master.title,
    ...(p.sub ? { sub: p.sub } : {}),
    category: master.category,
    duration: p.duration,
    chapters: [{ id: p.chapter.id, title: p.chapter.title, blocks }],
  };
}

/** The Certification lesson of a master: core plus cert-only, chaptered.
 *  A chapter's opening heading is dropped where the chapter title already
 *  says it. */
export function certView(master) {
  const c = master.cert;
  const chapters = master.chapters.map(ch => {
    const blocks = ch.blocks.filter(b => shows(b, CERT));
    const first = blocks[0];
    const dup = first && first.kind === "heading" && first.text === ch.title;
    return { id: ch.id, title: ch.title, blocks: dup ? blocks.slice(1) : blocks };
  }).filter(ch => ch.blocks.length);
  return {
    key: c.key,
    title: c.title || master.title,
    ...(c.sub ? { sub: c.sub } : {}),
    category: master.category,
    duration: c.duration,
    chapters,
  };
}

/** The Certification's own section titles, in order: what the Platform
 *  read's locked preview names. */
export function certOnlySections(certModule) {
  const out = [];
  (certModule?.chapters || []).forEach(ch => {
    // A chapter that is all cert lost its opening heading to its title.
    if (ch.blocks.every(b => b.depth === CERT)) out.push(ch.title);
    ch.blocks.forEach(b => {
      if (b.depth === CERT && b.kind === "heading" && b.level === 3) out.push(b.text);
    });
  });
  return [...new Set(out)];
}
